import { Search, Trash2, Loader2 } from "lucide-react"
import { useState } from "react"
import { format, isToday, isYesterday, differenceInDays } from "date-fns"
import { it } from "date-fns/locale"

interface Chat {
  id: string
  title: string
  lastMessage: string
  timestamp: Date
  unread: number
  group: string
}

interface ChatArchiveProps {
  chats: Chat[]
  selectedChatId: string | null
  isLoading?: boolean
  messagesCounts: Record<string, number>
  onSelectChat: (chatId: string) => void
  onDeleteChat?: (chatId: string) => void
}

function getTimeGroup(date: Date): string {
  if (isToday(date)) return "Oggi"
  if (isYesterday(date)) return "Ieri"
  if (differenceInDays(new Date(), date) <= 7) return "Questa settimana"
  return "Precedenti"
}

function formatChatTime(date: Date): string {
  if (isToday(date) || isYesterday(date)) {
    return format(date, "HH:mm", { locale: it })
  }
  return format(date, "d MMM", { locale: it })
}

export function ChatArchive({
  chats,
  selectedChatId,
  isLoading,
  messagesCounts,
  onSelectChat,
  onDeleteChat,
}: ChatArchiveProps) {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredChats = chats.filter((chat) =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Group by computed time group
  const groupOrder = ["Oggi", "Ieri", "Questa settimana", "Precedenti"]
  const grouped = filteredChats.reduce<Record<string, Chat[]>>((acc, chat) => {
    const group = getTimeGroup(chat.timestamp)
    if (!acc[group]) acc[group] = []
    acc[group].push(chat)
    return acc
  }, {})

  return (
    <div className="flex-1 flex flex-col min-h-0 px-4 pt-3 pb-4">
      {/* Search */}
      <div className="flex-shrink-0 mb-3">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
            style={{ color: "var(--hexa-text-muted)" }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca chat..."
            className="w-full pl-8 pr-3 py-2 text-xs focus:outline-none"
            style={{
              backgroundColor: "var(--hexa-surface-elevated)",
              border: "1px solid var(--hexa-border)",
              borderRadius: "10px",
              color: "var(--hexa-text-primary)",
            }}
          />
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto scrollbar-hexa">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-4 h-4 text-[var(--hexa-text-muted)] animate-spin" />
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p style={{ color: "var(--hexa-text-muted)", fontSize: "13px" }}>
              {chats.length === 0 ? "Nessuna conversazione" : "Nessun risultato"}
            </p>
          </div>
        ) : (
          groupOrder.map((group) => {
            const groupChats = grouped[group]
            if (!groupChats || groupChats.length === 0) return null

            return (
              <div key={group} className="mb-4">
                {/* Group label */}
                <p
                  className="px-2 mb-2"
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "var(--hexa-text-muted)",
                  }}
                >
                  {group}
                </p>

                {/* Chat items */}
                <div className="space-y-1">
                  {groupChats.map((chat) => {
                    const isActive = selectedChatId === chat.id
                    const msgCount = messagesCounts[chat.id] || 0
                    const timeStr = formatChatTime(chat.timestamp)

                    return (
                      <div
                        key={chat.id}
                        className="group relative flex items-center"
                      >
                        <button
                          onClick={() => onSelectChat(chat.id)}
                          className="w-full text-left transition-colors flex-1 min-w-0"
                          style={{
                            padding: "10px 12px",
                            borderRadius: "10px",
                            backgroundColor: isActive ? "rgba(123,63,255,0.1)" : "transparent",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = "var(--hexa-surface-hover)"
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = "transparent"
                          }}
                        >
                          <p
                            className="truncate"
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "var(--hexa-text-primary)",
                            }}
                          >
                            {chat.title}
                          </p>
                          <p
                            className="truncate mt-0.5"
                            style={{
                              fontSize: "11px",
                              color: "var(--hexa-text-muted)",
                            }}
                          >
                            {msgCount > 0 ? `${msgCount} messaggi · ${timeStr}` : timeStr}
                          </p>
                        </button>
                        {onDeleteChat && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteChat(chat.id)
                            }}
                            aria-label={`Elimina conversazione ${chat.title}`}
                            className="shrink-0 opacity-100 sm:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex items-center justify-center w-8 h-8 rounded-lg"
                            style={{ color: "var(--hexa-text-muted)" }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.15)"
                              e.currentTarget.style.color = "#ef4444"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent"
                              e.currentTarget.style.color = "var(--hexa-text-muted)"
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
